/* -----------------Globals--------------- */
import { Dialog, Text } from "react-native-paper";

/* -----------------Helpers & Hooks--------------- */
import { useAppTheme } from "@hooks/useAppTheme";

/* -----------------Child components--------------- */

import { events } from "@constants/tracking";
import { signOutAuth } from "@helpers/auth-provider";
import useAnalytics from "@hooks/useAnalytics";
import useAuthUser from "@hooks/useAuthUser";
import { useGlobalStore } from "@hooks/useGlobalStore";
import { storage } from "@memorang/helpers/storage";
import Button from "@memorang/ui/components/Button";
import DialogWrapper from "@memorang/ui/components/DialogWrapper";
import { useQueryClient } from "@tanstack/react-query";
/* -----------------Third parties--------------- */
import { trackLogout } from "analytics";
import { useRouter } from "expo-router";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

type Props = {
	visible: boolean;
	handleClose: () => void;
};
export const SignoutDialog = ({ visible, handleClose }: Props) => {
	const theme = useAppTheme();
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	const { email } = useAuthUser();

	const { explicitCallTrackCustomEvent } = useAnalytics();

	useEffect(() => {
		if (visible) {
			explicitCallTrackCustomEvent({
				eventName: events.dialogOpened,
				type: "SignOut",
			});
		}
	}, [visible, explicitCallTrackCustomEvent]);

	const router = useRouter();
	const setShowPaywall = useGlobalStore((state) => state.setShowPaywall);
	const handleSignout = async () => {
		explicitCallTrackCustomEvent({
			eventName: events.userLoggedOut,
			email,
		});
		setShowPaywall(false);
		queryClient.clear();
		storage.clearAll();
		await signOutAuth();
		trackLogout();
		handleClose();
		router.replace("/");
	};

	const handleDialogClose = () => {
		handleClose();
		explicitCallTrackCustomEvent({
			eventName: events.dialogClosed,
			type: "SignOut",
		});
	};
	return (
		<DialogWrapper
			visible={visible}
			handleClose={handleDialogClose}
			width={400}
		>
			<Dialog.Title>{t("account.signOut")}</Dialog.Title>
			<Dialog.Content>
				<Text variant="bodyMedium">{t("account.signOutConfirmation")}</Text>
			</Dialog.Content>
			<Dialog.Actions>
				<Button
					labelStyle={{
						color: theme.colors.onSurfaceVariant,
					}}
					mode="text"
					onPress={handleDialogClose}
				>
					{t("common.cancel")}
				</Button>
				<Button mode="text" onPress={handleSignout}>
					{t("common.ok")}
				</Button>
			</Dialog.Actions>
		</DialogWrapper>
	);
};
